import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../providers/AuthProvider'

const formatPhone = (p) => {
  if (!p) return '-'
  let clean = p.replace(/[^0-9]/g, '')
  if (clean.startsWith('82')) clean = '0' + clean.slice(2)
  // 010-XXXX-XXXX
  if (clean.length === 11) return clean.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
  // 010-XXX-XXXX (old format?) or others
  if (clean.length === 10) return clean.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
  return p
}

export default function MyPagePharmacist() {
  const { refreshProfile } = useAuth()
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [pharmacistProfile, setPharmacistProfile] = useState(null)
  const [myApplications, setMyApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadMyPage()
  }, [])

  async function loadMyPage() {
    setLoading(true)
    setError('')
    try {
      // ✅ 현재 로그인 유저
      const {
        data: { user }
      } = await supabase.auth.getUser()

      if (!user) {
        setUser(null)
        setProfile(null)
        setPharmacistProfile(null)
        setMyApplications([])
        setLoading(false)
        return
      }

      setUser(user)

      // ✅ 기본 프로필 (profiles)
      const { data: prof, error: profErr } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (profErr) {
        console.error('profiles load error', profErr)
      }
      setProfile(prof || null)

      // ✅ 약사 상세 정보 (pharmacists)
      const { data: pharmProf, error: pharmErr } = await supabase
        .from('pharmacists')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()

      if (pharmErr) {
        console.error('pharmacists load error', pharmErr)
      }
      setPharmacistProfile(pharmProf || null)

      // ✅ 내가 지원한 공고
      const { data: apps, error: appsErr } = await supabase
        .from('applications')
        .select(
          `
          id,
          status,
          created_at,
          request_message,
          job_posts:job_id (
            title,
            date,
            time,
            region,
            district,
            location
          )
        `
        )
        .eq('pharmacist_id', user.id)
        .order('created_at', { ascending: false })

      if (appsErr) {
        console.error('applications load error', appsErr)
      }

      setMyApplications(apps || [])
    } catch (e) {
      console.error(e)
      setError('마이페이지 정보를 불러오는 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl font-bold mb-2">약사 마이페이지</h2>
        <p className="text-sm text-gray-600">로그인이 필요합니다.</p>
      </div>
    )
  }

  const displayName =
    pharmacistProfile?.name ||
    profile?.name ||
    user.user_metadata?.name ||
    '-'

  const phone =
    pharmacistProfile?.phone || user.user_metadata?.phone || '-'

  const licenseNumber =
    pharmacistProfile?.license_number || '-' // 스키마에 맞게 사용

  // ✅ 주소 변경 로직
  const loadDaumPostcode = (callback) => {
    const existing = document.getElementById('daum-postcode-script')
    if (!existing) {
      const script = document.createElement('script')
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
      script.id = 'daum-postcode-script'
      script.onload = callback
      document.body.appendChild(script)
    } else {
      callback()
    }
  }

  const handleUpdateAddress = () => {
    loadDaumPostcode(() => {
      new window.daum.Postcode({
        oncomplete: (data) => {
          let fullAddress = data.address
          let extra = ''
          if (data.addressType === 'R') {
            if (data.bname !== '') extra += data.bname
            if (data.buildingName !== '') extra += extra !== '' ? ', ' + data.buildingName : data.buildingName
            fullAddress += extra !== '' ? ` (${extra})` : ''
          }

          // ✅ 상세 주소 입력 받기
          const detail = window.prompt('상세 주소를 입력해주세요 (예: 101동 101호):', '')
          if (detail) {
            fullAddress = `${fullAddress} ${detail}`
          }

          // 1. Geocoding
          if (window.kakao && window.kakao.maps && window.kakao.maps.services) {
            const geocoder = new window.kakao.maps.services.Geocoder()
            geocoder.addressSearch(data.address, async (result, status) => {
              if (status === window.kakao.maps.services.Status.OK) {
                const lat = parseFloat(result[0].y)
                const lng = parseFloat(result[0].x)

                // 2. Update Supabase
                try {
                  const { error: updateErr, count } = await supabase
                    .from('profiles')
                    .update({
                      address: fullAddress,
                      latitude: lat,
                      longitude: lng
                    }, { count: 'exact' }) // Count rows
                    .eq('user_id', user.id)

                  if (updateErr) throw updateErr

                  if (count === 0) {
                    // If no row updated, try upsert (recovery)
                    const { error: upsertErr } = await supabase.from('profiles').upsert({
                      user_id: user.id,
                      address: fullAddress,
                      latitude: lat,
                      longitude: lng,
                      role: 'pharmacist', // Default fallback
                      approval_status: 'pending' // Default fallback
                    })
                    if (upsertErr) throw upsertErr
                  }

                  alert('주소가 성공적으로 변경되었습니다.')

                  // ✅ 프로필 새로고침 (전역 상태 업데이트)
                  if (refreshProfile) refreshProfile()

                  loadMyPage() // Refresh
                } catch (e) {
                  console.error(e)
                  alert('주소 변경 중 오류가 발생했습니다.')
                }

              } else {
                alert('주소의 위치 정보를 찾을 수 없습니다.')
              }
            })
          } else {
            alert('지도 서비스를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.')
          }
        },
      }).open()
    })
  }

  return (
    <div className="max-w-5xl mx-auto grid gap-6">
      <h2 className="text-xl font-bold">약사 마이페이지</h2>

      {loading && <p className="text-sm text-gray-500">불러오는 중...</p>}
      {error && (
        <div className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {/* ✅ 내 정보 */}
      <section className="border rounded-xl p-5 bg-white grid gap-2">
        <h3 className="font-semibold text-base mb-1">내 정보</h3>
        {/* 이메일 숨김 처리 */}
        <p className="text-sm text-gray-700">
          <strong>이름:</strong> {displayName}
        </p>
        <p className="text-sm text-gray-700">
          <strong>연락처:</strong> {formatPhone(phone)}
        </p>
        <p className="text-sm text-gray-700">
          <strong>약사 면허번호:</strong> {licenseNumber}
        </p>
        <p className="text-sm text-gray-700">
          <strong>역할:</strong> {profile?.role || 'pharmacist'}
        </p>
        <div className="text-sm text-gray-700 flex items-center gap-2">
          <strong>주소:</strong>
          <span>{profile?.address || '-'}</span>
          <button
            onClick={handleUpdateAddress}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded border transition"
          >
            주소 변경
          </button>
        </div>
        {profile?.approval_status && (
          <p className="text-sm text-gray-700">
            <strong>승인 상태:</strong> {profile.approval_status}
          </p>
        )}
      </section>

      {/* ✅ 내가 지원한 공고 */}
      <section className="border rounded-xl p-5 bg-white grid gap-3">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold text-base">내 지원 내역</h3>
          <span className="text-xs text-gray-500">
            {myApplications.length}건
          </span>
        </div>

        {myApplications.length === 0 ? (
          <p className="text-sm text-gray-500">
            아직 지원한 공고가 없습니다.
          </p>
        ) : (
          <ul className="divide-y">
            {myApplications.map((app) => (
              <li key={app.id} className="py-2 grid gap-1 text-sm">
                <span className="font-medium">
                  {app.job_posts?.title || '(삭제된 공고)'}
                </span>
                <span className="text-gray-600">
                  {app.job_posts?.date} · {app.job_posts?.time}{' '}
                  {app.job_posts?.region && (
                    <>
                      · {app.job_posts.region}{' '}
                      {app.job_posts.district}
                    </>
                  )}
                </span>
                {app.request_message && (
                  <span className="text-gray-700">
                    메시지: {app.request_message}
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  지원일: {new Date(app.created_at).toLocaleString()}
                </span>
                <span className="text-xs">
                  상태:{' '}
                  <span
                    className={`px-2 py-0.5 rounded-full border ${app.status === 'approved'
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : app.status === 'closed'
                        ? 'bg-gray-100 text-gray-600 border-gray-200'
                        : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                      }`}
                  >
                    {app.status}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
